import React, { useEffect, useState } from "react";
import axios, { API_BASE_URL } from "../api/axios";
import ImageLoader from "../common/ImageLoader";
import "./Blogs.css";

const Blogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("ALL");
  const [loading, setLoading] = useState(true);

  const PUBLIC_DOMAIN = API_BASE_URL;

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/blogs");
      setBlogs(res.data || []);
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Get unique categories for filter pills
  const categories = [
    "ALL",
    ...Array.from(new Set(blogs.map((b) => b.category).filter(Boolean))),
  ];

  const filteredBlogs = blogs.filter((blog) => {
    const matchesSearch =
      blog.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      blog.abstract?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      selectedCategory === "ALL" || blog.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="blogs-section loading-page">
        <div className="loading-spinner"></div>
        <p>Loading our technical blogs...</p>
      </div>
    );
  }

  return (
    <section id="blogs-page" className="blogs-section">
      <div className="blogs-container">
        {/* Page Header */}
        <div className="section-header">
          <span className="section-tag">✦ KNOWLEDGE &amp; INSIGHTS</span>
          <h1 className="section-main-title">Our Technical Blogs</h1>
          <p className="section-description">
            Explore articles, research highlights, and emerging technology trends
            curated and written by members of our ACM Student Chapter.
          </p>

          {/* Search Bar */}
          <div className="blogs-search-wrapper">
            <i className="fas fa-search search-icon"></i>
            <input
              type="text"
              className="search-input"
              placeholder="Search blogs by title or keyword..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                className="search-clear-btn"
                onClick={() => setSearchTerm("")}
                aria-label="Clear Search"
              >
                &times;
              </button>
            )}
          </div>

          {/* Category Filter Pills */}
          {categories.length > 2 && (
            <div className="category-pills">
              {categories.map((cat) => (
                <button
                  key={cat}
                  className={`category-pill ${selectedCategory === cat ? "active" : ""}`}
                  onClick={() => setSelectedCategory(cat)}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Blog Grid */}
        <div className="blog-grid">
          {filteredBlogs.length > 0 ? (
            filteredBlogs.map((blog) => (
              <article key={blog.id} className="blog-card glass-panel glow-on-hover">
                <a
                  href={`${PUBLIC_DOMAIN}/${(blog.pdfPath || "").replace(/\\/g, "/")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="blog-link"
                >
                  <div className="blog-image-wrapper">
                    <ImageLoader
                      src={`${PUBLIC_DOMAIN}/${(blog.imagePath || "").replace(/\\/g, "/")}`}
                      alt={blog.title}
                      className="blog-image"
                    />
                    <div className="blog-read-badge">
                      <i className="far fa-file-pdf"></i>
                      <span>Read PDF</span>
                    </div>
                  </div>

                  <div className="blog-content">
                    <div className="blog-meta">
                      {blog.category && (
                        <span className="blog-category-badge">{blog.category}</span>
                      )}
                      {blog.date && (
                        <span className="blog-date">
                          <i className="far fa-calendar-alt"></i> {blog.date}
                        </span>
                      )}
                    </div>

                    <h2 className="blog-title">{blog.title}</h2>
                    <p className="blog-abstract">{blog.abstract}</p>

                    <div className="blog-card-footer">
                      <span className="read-more-text">
                        Read Full Article <i className="fas fa-arrow-right"></i>
                      </span>
                    </div>
                  </div>
                </a>
              </article>
            ))
          ) : (
            <div className="no-results glass-panel">
              <i className="fas fa-file-alt no-results-icon"></i>
              <h3>No Articles Found</h3>
              <p>Try adjusting your search query or filter category.</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Blogs;